import { User } from '@/@types/user'
import { getUserQueryOptions } from '@/utils/fetchers/user'
import useUser from '@/utils/hooks/useUser'
import { Box, type BoxProps, HoverCard, HoverCardProps, Stack } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import Avatar from './Avatar'
import FollowToggleButton from './FollowToggleButton'
import ScreenName from './ScreenName'
import UserProfileTag from './UserProfileTag'
import styles from './UserProfileHoverCard.module.css'

export interface UserProfileHoverCardProps extends HoverCardProps {
	data: User
	targetProps?: BoxProps
}

export default function UserProfileHoverCard({
	data,
	children,
	targetProps,
	...props
}: UserProfileHoverCardProps) {
	const currentUser = useUser(void 0)
	const { data: user = data } = useQuery({
		...getUserQueryOptions(data.username),
		enabled: Boolean(currentUser),
	})

	if (!currentUser) {
		return children
	}

	const { statsCount, bio, briefIntro, profileTags = [] } = user

	return (
		<HoverCard
			position="bottom-start"
			shadow="0 2px 12px 0 rgba(0, 0, 0, 0.24)"
			withinPortal
			radius={12}
			openDelay={500}
			{...props}
		>
			<HoverCard.Target>
				<Box display="inline" h="fit-content" {...targetProps}>
					{children}
				</Box>
			</HoverCard.Target>
			<HoverCard.Dropdown w={280} p={12} bg="var(--bg-body-1)" bd={0}>
				<Stack gap={12}>
					<div className={styles.header}>
						<Avatar
							data={user}
							className={styles.avatar}
							containerProps={{
								disabled: true,
							}}
						/>
						<div className={styles.content}>
							<ScreenName
								data={user}
								className={styles.screenName}
								containerProps={{
									disabled: true,
								}}
							/>

							<div className={styles.statsContainer}>
								<span className={styles.statsItem}>
									<span className={styles.statsValue}>{statsCount?.followingCount ?? 0}</span>
									<span className={styles.statsLabel}>关注</span>
								</span>
								<span className={styles.statsItem}>
									<span className={styles.statsValue}>{statsCount?.followedCount ?? 0}</span>
									<span className={styles.statsLabel}>被关注</span>
								</span>
							</div>
						</div>
					</div>

					{profileTags.length > 0 && (
						<div className={styles.tagsContainer}>
							{profileTags.map((item) => (
								<UserProfileTag data={item} key={JSON.stringify(item)} size="sm" />
							))}
						</div>
					)}

					{briefIntro && <div className={styles.bio}>{briefIntro}</div>}

					<FollowToggleButton data={user} size="sm" className={styles.followButton} />
				</Stack>
			</HoverCard.Dropdown>
		</HoverCard>
	)
}
